import React, { useCallback, useMemo, useRef } from "react"
import { EnumFasesSacola } from "../../../cardapio-sacola";
import { Box, Slide } from "@material-ui/core";
import { useStyles } from './identificacao-cliente-styles'
import { ClienteIcon } from "utils";
import { ContatoModel, PessoaModel, PessoaModelForm } from "api/cardapio/models/pessoa/pessoa-model";
import { guidEmpty } from "utils/guid-empty";
import { EnumTipoContato } from "models/enums/enum-tipo-contato";
import { usePedidos } from "services/providers/pedidos-provider";
import { useNotificacoes } from "components";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import { FasesHeader } from "../components/fases-header/fases-header";
import { IdentificacaoFragment } from "./components/identificacao-fragment/identificacao-fragment";
import { EnumConfigEmpresa } from "models/enums/enum-config-empresa";
import { CircularLoading } from "components/utils/circular-loading";
import { useEmpresaAtual } from "services/hooks/empresa";

export interface IdentificacaoClienteProps {
    faseSacola: EnumFasesSacola;
    handlePedidoFluxo: (nextOrBack: React.MutableRefObject<0 | 1>) => void
    fecharSacola: () => void
    tipoDelivery: boolean
    pedidoDelivery: boolean
    mesaId: string
    editarUsuarioEnderecoRef: boolean
    onClickProximaFase: (nextOrBack: React.MutableRefObject<0 | 1>, fase: EnumFasesSacola) => void
    onClickVoltarFase: (nextOrBack: React.MutableRefObject<0 | 1>, fase: EnumFasesSacola) => void
    voltarParaFaseFinalizarPedido: React.MutableRefObject<boolean>
    loadingInfoCliente: React.MutableRefObject<boolean>
}

export interface IdentificacaoClienteFormProps {
    cpfCnpj: string
    telefone: string
}

export const IdentificacaoCliente = ({
    faseSacola,
    handlePedidoFluxo,
    fecharSacola,
    pedidoDelivery,
    tipoDelivery,
    mesaId,
    editarUsuarioEnderecoRef,
    onClickProximaFase,
    onClickVoltarFase,
    voltarParaFaseFinalizarPedido,
    loadingInfoCliente
}: IdentificacaoClienteProps) => {
    const classes = useStyles()
    const { showErrorMessage } = useNotificacoes();
    const { setCliente, carregando: carregandoCalularFrete } = usePedidos();
    const { empresaId } = useParams<{ empresaId: string }>();
    const recarregaRef = useRef<boolean>(true)
    const nextOrBack = useRef<0 | 1>(1)
    const { getConfigByCod } = useEmpresaAtual()

    const carregando = [carregandoCalularFrete].includes(
        true
    );

    const isDelivery = pedidoDelivery && tipoDelivery && !mesaId

    const solicitarCliente = useMemo(() => {
        return getConfigByCod(EnumConfigEmpresa.soliClientesPorModeloTrabalho)?.valor || ''
    }, [getConfigByCod])

    const faseIdentificacao = faseSacola === EnumFasesSacola.IdentificarCliente

    const deepEqual = (a: any, b: any): boolean => {
        if (a === b) return true
        if (a === null || b === null) return false
        if (typeof a !== 'object' || typeof b !== 'object') return false
        if (Array.isArray(a) !== Array.isArray(b)) return false
        if (Array.isArray(a)) {
            if (a.length !== b.length) return false
            for (let i = 0; i < a.length; i++) {
                if (!deepEqual(a[i], b[i])) return false
            }
            return true
        }
        const ka = Object.keys(a)
        const kb = Object.keys(b)
        if (ka.length !== kb.length) return false
        for (const k of ka) {
            if (!Object.prototype.hasOwnProperty.call(b, k)) return false
            if (!deepEqual(a[k], b[k])) return false
        }
        return true
    }

    const proximo = useCallback(() => {
        if (voltarParaFaseFinalizarPedido.current) {
            onClickProximaFase(nextOrBack, EnumFasesSacola.ConfirmarInformacoes)
            setTimeout(() => {
                voltarParaFaseFinalizarPedido.current = false
            }, 100)
            return
        }
        handlePedidoFluxo(nextOrBack)
        recarregaRef.current = true
        setTimeout(() => {
            loadingInfoCliente.current = true
        }, 500)
    }, [handlePedidoFluxo, loadingInfoCliente, onClickProximaFase, voltarParaFaseFinalizarPedido])

    const voltar = useCallback(() => {
        if (voltarParaFaseFinalizarPedido.current) {
            onClickVoltarFase(nextOrBack, EnumFasesSacola.ConfirmarInformacoes)
            voltarParaFaseFinalizarPedido.current = false
            return
        }
        onClickVoltarFase(nextOrBack, EnumFasesSacola.Sacola)
        recarregaRef.current = true
    }, [voltarParaFaseFinalizarPedido, onClickVoltarFase])

    const handleProximoPasso = useCallback(async () => {
        if (isDelivery) {
            onClickProximaFase(nextOrBack, EnumFasesSacola.InformarEndereco)
            return
        }
        proximo()
    }, [isDelivery, onClickProximaFase, proximo])

    const submitNovaPessoa = async (model: PessoaModelForm) => {
        try {
            const telefone: ContatoModel | undefined = !isEmpty(model.telefone.replace(/\D/g, "")) ? {
                id: guidEmpty(),
                pessoaId: guidEmpty(),
                tipo: EnumTipoContato.TELEFONE,
                valor: model.telefone.replace(/\D/g, "")
            } : undefined
            const email: ContatoModel | undefined = !isEmpty(model.email) ? {
                id: guidEmpty(),
                pessoaId: guidEmpty(),
                tipo: EnumTipoContato.EMAIL,
                valor: model.email
            } : undefined

            const modelFormPadrao = new PessoaModelForm()
            if (deepEqual(modelFormPadrao, model)) {
                proximo()
                return
            }

            const pessoa: PessoaModel = {
                ...new PessoaModel(),
                nome: model.nome,
                cpfcnpj: model.cpfcnpj,
                dtNasc: model.dtNasc,
                contatos: [telefone, email].filter(Boolean) as ContatoModel[],
                sexo: model.sexo,
            }
            setCliente(pessoa)
            await handleProximoPasso()
        } catch (err: any) {
            showErrorMessage(err.message);
        }
    };

    return (
        <Slide direction={nextOrBack.current === 1 ? "left" : 'right'} mountOnEnter unmountOnExit in={faseIdentificacao}>
            <Box height={"100%"} width={"100%"} className={classes.boxContainer}>
                <FasesHeader
                    titulo="DADOS DO CLIENTE"
                    icon={ClienteIcon}
                    voltar={voltar}
                />
                <Box className={classes.content}>
                    {carregando && <CircularLoading tipo="FULLSIZED" />}
                    <IdentificacaoFragment
                        solicitarCliente={solicitarCliente}
                        submitNovaPessoa={submitNovaPessoa}
                        isDelivery={isDelivery}
                        empresaId={empresaId}
                        faseIdentificacao={faseIdentificacao}
                    />
                </Box>
            </Box>
        </Slide>
    )
}
